import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDownIcon, ChevronUpIcon, FunnelIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, MapPinIcon, PhoneIcon, GlobeAltIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { AppLayout } from '@/layouts/AppLayout';
import { api } from '@/lib/api';
import { useAuth, useIsAdmin } from '@/hooks/useAuth';
import type { License, QueryFilters } from '@/types';
import ExportModal from '@/components/ExportModal';
import type { ExportField } from '@/types/exportFields';
import { logger } from '@/lib/logger';
import { secureStorage } from '@/lib/secureStorage';
import { sanitizer } from '@/lib/sanitizer';

interface LiquorStoresPageState {
  licenses: License[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

interface LiquorFilters {
  counties: string[];
  cities: string[];
  searchTerm: string;
  hasPhone: boolean;
}

const ITEMS_PER_PAGE = 50;

export const LiquorStoresPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const [state, setState] = useState<LiquorStoresPageState>({
    licenses: [],
    loading: true,
    error: null,
    totalCount: 0,
    currentPage: 1,
    totalPages: 0,
  });

  const [filters, setFilters] = useState<LiquorFilters>({
    counties: [],
    cities: [],
    searchTerm: '',
    hasPhone: false,
  });

  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Load filters from secure storage on component mount
  useEffect(() => {
    try {
      const saved = secureStorage.getFilters('liquorStores');
      if (saved && typeof saved === 'object') {
        logger.debug('Loaded liquor stores filters from secure storage');
        setFilters(saved);
      } else if (saved) {
        logger.warn('Invalid filter data format, using defaults');
      }
    } catch (error) {
      logger.warn('Failed to load filters from secure storage, using defaults', error);
      // Clear any corrupted data and continue with defaults
      secureStorage.remove('filters_liquorStores');
    }
    setFiltersLoaded(true);
  }, []);

  // Auto-expand filters if any filters are already selected
  const hasActiveFilters = (filters: LiquorFilters): boolean => {
    return filters.counties.length > 0 || 
           filters.cities.length > 0 || 
           filters.searchTerm.length > 0 || 
           filters.hasPhone;
  };

  const [showFilters, setShowFilters] = useState(false);

  // Auto-expand filter panel when filters are loaded from storage
  useEffect(() => {
    if (hasActiveFilters(filters)) {
      setShowFilters(true);
      logger.debug('Auto-expanding filter panel due to active filters');
    }
  }, [filters]);
  const [enriching, setEnriching] = useState(false);
  const [enrichmentResult, setEnrichmentResult] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadResult, setDownloadResult] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Total counts for statistics (not just current page)
  const [totalWithPhone, setTotalWithPhone] = useState(0);
  const [totalWithWebsite, setTotalWithWebsite] = useState(0);
  // California counties (matching database format - ALL CAPS)
  const counties = [
    'ALAMEDA', 'ALPINE', 'AMADOR', 'BUTTE', 'CALAVERAS', 'COLUSA', 'CONTRA COSTA',
    'DEL NORTE', 'EL DORADO', 'FRESNO', 'GLENN', 'HUMBOLDT', 'IMPERIAL', 'INYO',
    'KERN', 'KINGS', 'LAKE', 'LASSEN', 'LOS ANGELES', 'MADERA', 'MARIN',
    'MARIPOSA', 'MENDOCINO', 'MERCED', 'MODOC', 'MONO', 'MONTEREY', 'NAPA',
    'NEVADA', 'ORANGE', 'PLACER', 'PLUMAS', 'RIVERSIDE', 'SACRAMENTO', 'SAN BENITO',
    'SAN BERNARDINO', 'SAN DIEGO', 'SAN FRANCISCO', 'SAN JOAQUIN', 'SAN LUIS OBISPO',
    'SAN MATEO', 'SANTA BARBARA', 'SANTA CLARA', 'SANTA CRUZ', 'SHASTA', 'SIERRA',
    'SISKIYOU', 'SOLANO', 'SONOMA', 'STANISLAUS', 'SUTTER', 'TEHAMA', 'TRINITY',
    'TULARE', 'TUOLUMNE', 'VENTURA', 'YOLO', 'YUBA'
  ];

  const cities = [
    'ALAMEDA', 'ALHAMBRA', 'ANAHEIM', 'ANTIOCH', 'BAKERSFIELD', 'BALDWIN PARK',
    'BELLFLOWER', 'BERKELEY', 'BUENA PARK', 'BURBANK', 'CAMARILLO', 'CARLSBAD',
    'CARSON', 'CHICO', 'CHINO', 'CHINO HILLS', 'CHULA VISTA', 'CITRUS HEIGHTS',
    'CLOVIS', 'COMPTON', 'CONCORD', 'CORONA', 'COSTA MESA', 'DALY CITY',
    'DAVIS', 'DOWNEY', 'EL CAJON', 'EL MONTE', 'ELK GROVE', 'ENCINITAS',
    'ESCONDIDO', 'FAIRFIELD', 'FOLSOM', 'FONTANA', 'FREMONT', 'FRESNO',
    'FULLERTON', 'GARDEN GROVE', 'GLENDALE', 'HAWTHORNE', 'HAYWARD', 'HEMET',
    'HESPERIA', 'HUNTINGTON BEACH', 'INDIO', 'INGLEWOOD', 'IRVINE', 'LA HABRA',
    'LAGUNA NIGUEL', 'LAKE FOREST', 'LAKEWOOD', 'LANCASTER', 'LIVERMORE', 'LODI',
    'LONG BEACH', 'LOS ANGELES', 'MADERA', 'MANTECA', 'MENIFEE', 'MERCED',
    'MILPITAS', 'MISSION VIEJO', 'MODESTO', 'MONTEBELLO', 'MONTEREY', 'MONTEREY PARK',
    'MORENO VALLEY', 'MOUNTAIN VIEW', 'MURRIETA', 'NAPA', 'NEWPORT BEACH', 'NORWALK',
    'OAKLAND', 'OCEANSIDE', 'ONTARIO', 'ORANGE', 'OXNARD', 'PALMDALE',
    'PALO ALTO', 'PASADENA', 'PETALUMA', 'PICO RIVERA', 'PITTSBURG', 'PLEASANTON',
    'POMONA', 'RANCHO CUCAMONGA', 'REDDING', 'REDLANDS', 'REDWOOD CITY', 'RIALTO',
    'RICHMOND', 'RIVERSIDE', 'ROSEVILLE', 'SACRAMENTO', 'SALINAS', 'SAN BERNARDINO',
    'SAN CLEMENTE', 'SAN DIEGO', 'SAN FRANCISCO', 'SAN JOSE', 'SAN LEANDRO', 'SAN MARCOS',
    'SAN MATEO', 'SAN RAFAEL', 'SAN RAMON', 'SANTA ANA', 'SANTA BARBARA', 'SANTA CLARA',
    'SANTA CLARITA', 'SANTA CRUZ', 'SANTA MARIA', 'SANTA MONICA', 'SANTA ROSA', 'SIMI VALLEY',
    'SOUTH GATE', 'SOUTH SAN FRANCISCO', 'STOCKTON', 'SUNNYVALE', 'TEMECULA', 'THOUSAND OAKS',
    'TORRANCE', 'TRACY', 'TULARE', 'TURLOCK', 'UNION CITY', 'UPLAND',
    'VACAVILLE', 'VALLEJO', 'VENTURA', 'VICTORVILLE', 'VISALIA', 'VISTA',
    'WALNUT CREEK', 'WEST COVINA', 'WESTMINSTER', 'WHITTIER', 'YORBA LINDA', 'YUBA CITY'
  ];

  // Load initial data when page or filters change, but only after filters are loaded
  useEffect(() => {
    if (filtersLoaded) {
      loadLiquorStores();
      fetchTotalCounts();
    }
  }, [state.currentPage, filters, filtersLoaded]);

  const loadLiquorStores = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Build query filters for Full Liquor Only licenses (excluding chains)
      const queryFilters: QueryFilters = {
        license_types: ['21'], // Type 21 - Off-Sale General (Full Liquor)
        license_type_status: ['ACTIVE'], // Active licenses only
        premise_county: filters.counties.length > 0 ? filters.counties : undefined,
        cities: filters.cities.length > 0 ? filters.cities : undefined,
        business_name: filters.searchTerm || undefined,
        exclude_chains: true, // Automatically exclude chains for insurance targeting
        hasPhone: filters.hasPhone || undefined, // Filter for records with phone numbers
        limit: ITEMS_PER_PAGE,
        offset: (state.currentPage - 1) * ITEMS_PER_PAGE,
      };

      const response = await api.queryData(queryFilters);

      setState(prev => ({
        ...prev,
        licenses: response.licenses,
        totalCount: response.total,
        totalPages: Math.ceil(response.total / ITEMS_PER_PAGE),
        loading: false,
      }));
    } catch (error) {
      console.error('Error loading liquor stores:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load liquor stores',
        loading: false,
      }));
    }
  };

  const fetchTotalCounts = async () => {
    try {
      // Build the same query filters but without pagination to get all results
      const queryFilters: QueryFilters = {
        license_types: ['21'], // Type 21 - Off-Sale General (Full Liquor)
        license_type_status: ['ACTIVE'], // Active licenses only
        premise_county: filters.counties.length > 0 ? filters.counties : undefined,
        cities: filters.cities.length > 0 ? filters.cities : undefined,
        business_name: filters.searchTerm || undefined,
        exclude_chains: true, // Automatically exclude chains
        hasPhone: filters.hasPhone || undefined,
        limit: 10000, // Large limit to get all results
        offset: 0,
      };

      const response = await api.queryData(queryFilters);

      const licenses = response.licenses || [];
      setTotalWithPhone(licenses.filter(l => l.google_phone).length);
      setTotalWithWebsite(licenses.filter(l => l.google_website).length);
    } catch (error) {
      console.error('Error fetching total counts:', error);
    }
  };

  // No longer needed - using static comprehensive lists

  const handleFilterChange = (key: keyof LiquorFilters, value: string | boolean | string[]) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    
    setFilters(newFilters);
    
    // Save filters to secure storage for persistence across page refreshes
    try {
      secureStorage.setFilters('liquorStores', newFilters);
      logger.debug('Saved liquor stores filters to secure storage');
    } catch (error) {
      logger.warn('Failed to save filters to secure storage', error);
    }
    
    // Reset to first page when filters change
    setState(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleCountyToggle = (county: string) => {
    const newCounties = filters.counties.includes(county)
      ? filters.counties.filter(c => c !== county)
      : [...filters.counties, county];
    handleFilterChange('counties', newCounties);
  };

  const handleCityToggle = (city: string) => {
    const newCities = filters.cities.includes(city)
      ? filters.cities.filter(c => c !== city)
      : [...filters.cities, city];
    handleFilterChange('cities', newCities);
  };

  const handleDownload = () => {
    // Open the export modal instead of directly downloading
    setShowExportModal(true);
  };

  const handleExportWithFields = async (selectedFields: string[], exportFilters: QueryFilters) => {
    try {
      setDownloading(true);
      setDownloadResult(null);
      
      logger.export('Starting CSV download', { 
        selectedFieldCount: selectedFields.length,
        hasFilters: Object.keys(exportFilters).length > 0
      });
      
      const queryFilters: QueryFilters = {
        license_types: ['21'], // Type 21 - Off-Sale General (Full Liquor)
        license_type_status: ['ACTIVE'], // Active licenses only
        premise_county: filters.counties.length > 0 ? filters.counties : undefined,
        cities: filters.cities.length > 0 ? filters.cities : undefined,
        business_name: filters.searchTerm || undefined,
        exclude_chains: true, // Exclude chains for CSV export too
        hasPhone: filters.hasPhone || undefined, // Include phone filter
      };

      logger.export('Prepared query filters for CSV export');

      // Call the CSV export API with selected fields
      const blob = await api.exportFilteredCSV(queryFilters, selectedFields);
      
      // Generate filename with current date and filter info
      const today = new Date().toISOString().split('T')[0];
      const cityFilter = filters.cities.length > 0 ? `_${filters.cities[0].toLowerCase().replace(/\s+/g, '_')}` : '';
      const phoneFilter = filters.hasPhone ? '_with_phone' : '';
      const filename = `liquor_stores${cityFilter}${phoneFilter}_${today}.csv`;
      
      // Use the File System Access API if available, otherwise fall back to download link
      if ('showSaveFilePicker' in window) {
        try {
          // Modern browsers - show Save As dialog
          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'CSV files',
              accept: { 'text/csv': ['.csv'] }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          logger.export('CSV saved successfully via File System Access API');
          setDownloadResult(`Successfully saved ${state.totalCount} records to ${fileHandle.name}`);
        } catch (error: any) {
          if (error.name === 'AbortError') {
            // User cancelled the save dialog
            logger.export('User cancelled save dialog');
            setDownloadResult('Download cancelled by user');
            setTimeout(() => setDownloadResult(null), 3000);
            return;
          }
          throw error; // Re-throw other errors to fall back to download link
        }
      } else {
        // Fallback for older browsers - automatic download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        
        // Make the link invisible and add to DOM
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 100);
        
        logger.export('CSV download completed via fallback method');
        setDownloadResult(`Successfully downloaded ${state.totalCount} records to ${filename}`);
      }
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => setDownloadResult(null), 5000);
      
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setDownloadResult(`Error: ${error instanceof Error ? error.message : 'Failed to download CSV'}`);
      
      // Auto-dismiss error message after 8 seconds
      setTimeout(() => setDownloadResult(null), 8000);
    } finally {
      setDownloading(false);
    }
  };

  const formatAddress = (license: License) => {
    const parts = [
      license.premise_address,
      license.premise_city,
      license.premise_zip
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getEnrichmentStatus = (license: License) => {
    const hasPhone = !!license.google_phone;
    const hasWebsite = !!license.google_website;
    
    if (hasPhone && hasWebsite) return { status: 'complete', color: 'green' };
    if (hasPhone || hasWebsite) return { status: 'partial', color: 'yellow' };
    return { status: 'none', color: 'gray' };
  };

  const handleMarkAsChain = async (licenseId: string, isChain: boolean) => {
    try {
      const response = await api.markLicenseAsChain(licenseId, isChain);
      if (response.success) {
        // Update the license in the current state
        setState(prev => ({
          ...prev,
          licenses: prev.licenses.map(license => 
            license.id === licenseId 
              ? { ...license, is_chain: isChain, manual_chain_override: true }
              : license
          )
        }));
        
        // Show success message
        alert(`Successfully marked as ${isChain ? 'chain' : 'independent'}!`);
      } else {
        throw new Error(response.error || 'Failed to update chain status');
      }
    } catch (error) {
      console.error('Error marking chain status:', error);
      alert('Failed to update chain status. Please try again.');
    }
  };

  const handleEnrichment = async () => {
    logger.userAction('Enrichment button clicked');
    if (!isAdmin) {
      alert('Only administrators can trigger enrichment.');
      return;
    }

    if (state.totalCount === 0) {
      alert('No records to enrich. Please adjust your filters.');
      return;
    }

    try {
      setEnriching(true);
      setEnrichmentResult(null);
      logger.debug('Starting enrichment process', { totalCount: state.totalCount });
      
      // Convert filters to query format and sanitize
      const queryFilters: QueryFilters = sanitizer.sanitizeFilters({
        license_types: ['21'], // Type 21 - Off-Sale General (Full Liquor)
        license_type_status: ['ACTIVE'], // Active licenses only
        premise_county: filters.counties.length > 0 ? filters.counties : undefined,
        cities: filters.cities.length > 0 ? filters.cities : undefined,
        business_name: filters.searchTerm || undefined,
        exclude_chains: true, // Automatically exclude chains
        hasPhone: filters.hasPhone || undefined,
        limit: state.totalCount, // Use total count to enrich all results
        offset: 0
      });
      
      logger.debug('Prepared sanitized query filters for enrichment');
      const response = await api.triggerFilteredEnrichment(queryFilters);
      logger.debug('Enrichment API response received');
      
      if (response.success) {
        const jobId = response.data?.jobId || 'unknown';
        setEnrichmentResult(`Enrichment job started (ID: ${jobId})! Processing records in the background. This may take several minutes depending on the number of records. You can continue using the app - the data will be updated automatically as enrichment completes.`);
      } else {
        setEnrichmentResult(`Error: ${response.error || 'Failed to trigger enrichment'}`);
      }
    } catch (error) {
      logger.error('Error triggering enrichment', error);
      setEnrichmentResult(`Error: ${error instanceof Error ? error.message : 'Failed to trigger enrichment'}`);
    } finally {
      setEnriching(false);
      logger.debug('Enrichment process completed successfully');
    }
  };

  return (
    <AppLayout currentPath="/liquor-stores">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              🍾 Liquor Stores
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Independent Full Liquor License holders - excludes chains like 7-Eleven, Shell, Chevron. Perfect for insurance targeting.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none space-x-3">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              <FunnelIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Filters
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading || state.totalCount === 0}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              {downloading ? 'Downloading...' : `Download ${state.totalCount} Records`}
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={handleEnrichment}
                disabled={enriching || state.totalCount === 0}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SparklesIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                {enriching ? 'Enriching...' : `Enrich ${state.totalCount} Records`}
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Search Term */}
              <div>
                <label htmlFor="search" className="block text-sm font-medium leading-6 text-gray-900">
                  Search Business Name
                </label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="text"
                    name="search"
                    id="search"
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    placeholder="Enter business name..."
                  />
                </div>
              </div>

              {/* Counties Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Counties ({filters.counties.length} selected)
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white">
                  {counties.map(county => (
                    <label key={county} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.counties.includes(county)}
                        onChange={() => handleCountyToggle(county)}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-900">{county}</span>
                    </label>
                  ))}
                </div>
                {filters.counties.length > 0 && (
                  <button
                    onClick={() => handleFilterChange('counties', [])}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear all counties
                  </button>
                )}
              </div>

              {/* Cities Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Cities ({filters.cities.length} selected)
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white">
                  {cities.map(city => (
                    <label key={city} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.cities.includes(city)}
                        onChange={() => handleCityToggle(city)}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-900">{city}</span>
                    </label>
                  ))}
                </div>
                {filters.cities.length > 0 && (
                  <button
                    onClick={() => handleFilterChange('cities', [])}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear all cities
                  </button>
                )}
              </div>

              {/* Enrichment Filters */}
              <div className="space-y-2">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Enrichment Status
                </label>
                <div className="space-y-2">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.hasPhone}
                      onChange={(e) => handleFilterChange('hasPhone', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">Has Phone</span>
                  </label>

                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enrichment Result */}
        {enrichmentResult && (
          <div className={`mt-6 border rounded-lg p-4 ${
            enrichmentResult.startsWith('Error') 
              ? 'bg-red-50 border-red-200' 
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {enrichmentResult.startsWith('Error') ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  enrichmentResult.startsWith('Error') 
                    ? 'text-red-800' 
                    : 'text-blue-800'
                }`}>
                  {enrichmentResult.startsWith('Error') ? 'Enrichment Error' : 'Enrichment Status'}
                </p>
                <p className={`text-sm ${
                  enrichmentResult.startsWith('Error') 
                    ? 'text-red-700' 
                    : 'text-blue-700'
                }`}>
                  {enrichmentResult}
                </p>
              </div>
              <div className="ml-auto">
                <button
                  type="button"
                  onClick={() => setEnrichmentResult(null)}
                  className={`text-sm font-medium ${
                    enrichmentResult.startsWith('Error') 
                      ? 'text-red-600 hover:text-red-500' 
                      : 'text-blue-600 hover:text-blue-500'
                  }`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Download Result */}
        {downloadResult && (
          <div className={`mt-6 border rounded-lg p-4 ${
            downloadResult.startsWith('Error') 
              ? 'bg-red-50 border-red-200' 
              : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {downloadResult.startsWith('Error') ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  downloadResult.startsWith('Error') 
                    ? 'text-red-800' 
                    : 'text-green-800'
                }`}>
                  {downloadResult.startsWith('Error') ? 'Download Error' : 'Download Complete'}
                </p>
                <p className={`text-sm ${
                  downloadResult.startsWith('Error') 
                    ? 'text-red-700' 
                    : 'text-green-700'
                }`}>
                  {downloadResult}
                </p>
              </div>
              <div className="ml-auto">
                <button
                  type="button"
                  onClick={() => setDownloadResult(null)}
                  className={`text-sm font-medium ${
                    downloadResult.startsWith('Error') 
                      ? 'text-red-600 hover:text-red-500' 
                      : 'text-green-600 hover:text-green-500'
                  }`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}



        {/* Statistics */}
        <div className="mt-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <MapPinIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Independent Liquor Stores</dt>
                      <dd className="text-lg font-medium text-gray-900">{(state.totalCount || 0).toLocaleString()}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <PhoneIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">With Phone Numbers</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {totalWithPhone}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <GlobeAltIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">With Websites</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {totalWithWebsite}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">
                        Business Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        Address
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        Phone
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        Website
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        Status
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {state.loading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading liquor stores...
                        </td>
                      </tr>
                    ) : state.error ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-sm text-red-500">
                          {state.error}
                        </td>
                      </tr>
                    ) : state.licenses.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                          No liquor stores found matching your filters.
                        </td>
                      </tr>
                    ) : (
                      state.licenses.map((license) => {
                        const enrichmentStatus = getEnrichmentStatus(license);
                        return (
                          <tr key={license.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {license.dba_name || license.business_name}
                                </div>
                                {license.dba_name && license.business_name && license.dba_name !== license.business_name && (
                                  <div className="text-gray-500 text-xs">
                                    {license.business_name}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {formatAddress(license)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {license.google_phone || '-'}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500">
                              {license.google_website ? (
                                <a
                                  href={license.google_website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-900 truncate block max-w-xs"
                                >
                                  {license.google_website}
                                </a>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                enrichmentStatus.color === 'green'
                                  ? 'bg-green-50 text-green-700 ring-green-600/20'
                                  : enrichmentStatus.color === 'yellow'
                                  ? 'bg-yellow-50 text-yellow-800 ring-yellow-600/20'
                                  : 'bg-gray-50 text-gray-600 ring-gray-500/10'
                              }`}>
                                {enrichmentStatus.status === 'complete' ? 'Complete' :
                                 enrichmentStatus.status === 'partial' ? 'Partial' : 'Basic'}
                              </span>
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                onClick={() => handleMarkAsChain(license.id, !license.is_chain)}
                                className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                                  license.is_chain
                                    ? 'text-green-700 bg-green-100 hover:bg-green-200'
                                    : 'text-red-700 bg-red-100 hover:bg-red-200'
                                }`}
                              >
                                {license.is_chain ? 'Mark Independent' : 'Mark as Chain'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {state.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => setState(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                disabled={state.currentPage === 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
              >
                Previous
              </button>
              <button
                onClick={() => setState(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                disabled={state.currentPage === state.totalPages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{((state.currentPage - 1) * ITEMS_PER_PAGE) + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(state.currentPage * ITEMS_PER_PAGE, state.totalCount)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{state.totalCount}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => setState(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                    disabled={state.currentPage === 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, state.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setState(prev => ({ ...prev, currentPage: pageNum }))}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          pageNum === state.currentPage
                            ? 'z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setState(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                    disabled={state.currentPage === state.totalPages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExportWithFields}
          filters={{
            license_types: ['21'],
            license_type_status: ['ACTIVE'],
            premise_county: filters.counties.length > 0 ? filters.counties : undefined,
            cities: filters.cities.length > 0 ? filters.cities : undefined,
            business_name: filters.searchTerm || undefined,
            exclude_chains: true,
            hasPhone: filters.hasPhone || undefined,
          }}
          totalCount={state.totalCount}
          isExporting={downloading}
        />
      </div>
    </AppLayout>
  );
};
